
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import withBodyScrollDisabled from './withBodyScrollDisabled'
import getProp from '@f/get-prop'
import { connect } from 'react-redux'
import { lockoutModalShown, lockoutModalClicked, lockoutOptionClicked } from 'analytics'
import { LOCKOUT_REASON_MTUS, LOCKOUT_REASON_PAST_DUE } from 'shared/constants'
import {
  addCreditCardAndUpdateBillingPlan, addCreditCardByWorkspaceSlug,
  changeLockoutStep, fetchLockouts, fetchPlans, deleteWorkspaceBySlug,
  updateChurnSurvey
} from 'actions'
import {
  getActiveWorkspace,
  getBilling,
  getBillingPlan,
  getChurnSurvey,
  getLockoutByWorkspaceSlug,
  getLockoutsApp,
  getPlanById,
  getSortedPlans,
  getWorkspace
} from 'selectors'
import { Display1, Body2 } from './typography'
import UpgradePrompt from './UpgradePrompt'
import Copy from './Copy'
import Space from './Space'
import Button from './Button'
import Block from './Block'
import Link from './Link'
import { ASSET_BASE_URL } from 'config'
import './Lockout.css'
import ChurnSurvey from './ChurnSurvey'
import Overlay from './Overlay'
import Confirm from './Confirm'
import { surveyViewed, surveySubmitted, surveyClosed } from '../pages/workspace-settings/analytics'

class MtusLockout extends Component {
  constructor () {
    super()
    this.state = { showWorkspaceDeleteConfirm: false }
    this.onDeleteClick = this.onDeleteClick.bind(this)
    this.onChurnSurveySubmit = this.onChurnSurveySubmit.bind(this)
    this.onChurnSurveyClose = this.onChurnSurveyClose.bind(this)
  }

  render () {
    const { step, onStepChange, onPlanClick, churnSurvey } = this.props
    const { showWorkspaceDeleteConfirm } = this.state
    const self = this
    switch (step) {
      case 'initial_prompt':
        return renderInitialPrompt()
      case 'upgrade_prompt':
        return renderUpgradePrompt()
      case 'credit_card':
        return <noscript />
      default:
        throw new Error('Must provide a valid lockout step')
    }

    function renderInitialPrompt () {
      return (
        <div>
          <div className='Lockout-content'>
            <Block spaceTop={28} spaceLeft={64} spaceRight={64}>
              <Display1>MTU Limit Reached</Display1>
              <Space value={35} />
              <img className='Lockout-mtuGraph' src={`${ASSET_BASE_URL}/ui/lockouts/mtu-graph.svg`} />
              <Space value={35} />
              <p>
                The developer plan supports up to 1,000 MTUs and your workspace has
                reached the limit. Please upgrade your plan and enter a credit card to
                continue using Segment.
              </p>
              <Space value={32} />
              <Button onClick={() => onStepChange('upgrade_prompt')}>Select a Plan</Button>
              <Space value={24} />
              <Body2>Questions? <Link href='/contact' rel='external'>Contact us</Link></Body2>
              <Space value={8} />
              <Body2><Link onClick={self.onDeleteClick}>Delete this workspace.</Link> This is irreversible.</Body2>
            </Block>
          </div>

          {churnSurvey.isShowingChurnModal &&
            <ChurnSurvey
              title='Delete Workspace'
              copy='We’re sorry to see you go. Before you go, can you tell us why you are cancelling?'
              confirmText='Submit'
              onClose={self.onChurnSurveyClose}
              onConfirm={self.onChurnSurveySubmit}
              onChange={newChurnSurvey => self.props.updateChurnSurvey(newChurnSurvey)}
              churnSurvey={churnSurvey}
              disabled={!churnSurvey.surveyComplete}
            />
          }
          {showWorkspaceDeleteConfirm &&
            <Overlay visible>
              <Confirm title='Delete Workspace' confirmText='Yep, delete this workspace!' onClose={() => self.setState({ showWorkspaceDeleteConfirm: false })} onConfirm={() => self.props.deleteWorkspace()} isDangerous>
                <p>
                  Are you sure you want to delete this workspace? All sources within this workspace will also be deleted. There’s no coming back from this!
                </p>
              </Confirm>
            </Overlay>
          }
        </div>
      )
    }

    function renderUpgradePrompt () {
      const planOverrides = {
        team: {
          ctaAddendum: 'Credit card required'
        }
      }

      return (
        <UpgradePrompt
          hasOverlay={false}
          headline='Upgrade from the Developer plan to unlock unlimited MTUs'
          subhead="Your monthly cost is based on MTUs, which may fluctuate each month based on your user activity. If you'd like to have an annual contract, the Business plan is right for you."
          plans={['team', 'business']}
          planOverrides={planOverrides}
          onPlanClick={plan => {
            onPlanClick(plan)
            // Only continue to credit-card step when selecting the team plan
            if (plan === 'team') onStepChange('credit_card')
          }}
          onClose={() => onStepChange('initial_prompt')} />
      )
    }
  }

  onDeleteClick () {
    surveyViewed('Workspace Deleted', this.props.workspace)

    // record timestamp to help capture how long it takes to complete survey
    this.props.updateChurnSurvey({
      isShowingChurnModal: true,
      timestamp: new Date().getTime()
    })
  }

  onChurnSurveySubmit () {
    surveySubmitted('Workspace Deleted', this.props.workspace, this.props.churnSurvey)
    this.props.updateChurnSurvey({ isShowingChurnModal: false })
    this.setState({ showWorkspaceDeleteConfirm: true })
  }

  onChurnSurveyClose () {
    surveyClosed('Workspace Deleted', this.props.workspace, this.props.churnSurvey)
    this.props.updateChurnSurvey({ isShowingChurnModal: false })
  }
}

const PastDueLockout = props => {
  const { step, onStepChange } = props

  if (step !== 'initial_prompt') return <noscript />

  return (
    <div className='Lockout-content'>
      <Block spaceTop={32} spaceLeft={64} spaceRight={64}>
        <Display1>Your Account is Locked</Display1>
        <Space value={8} />
        <p>We are having trouble charging your credit card for this month’s bill. Please update your credit card information to continue using Segment.</p>
        <Space value={32} />
        <Button extras='rounded' onClick={() => onStepChange('credit_card')}>Update Credit Card</Button>
        <Space value={32} />
        <Body2>Questions? <Link href='/contact' rel='external'>Contact us</Link></Body2>
      </Block>
    </div>
  )
}

const WarningLockout = props => (
  <div className='Lockout-content'>
    <Block spaceTop={32} spaceBottom={32} spaceLeft={64} spaceRight={64}>
      <Display1>Your Account is Locked</Display1>
      <Space value={8} />
      <Copy>{props.message || 'We have been trying to get in touch about an outstanding issue. <br/>Please contact us at [friends@segment.com](mailto:friends@segment.com) to resolve the issue and continue using Segment.'}</Copy>
    </Block>
  </div>
)

const lockouts = {
  [LOCKOUT_REASON_MTUS]: MtusLockout,
  [LOCKOUT_REASON_PAST_DUE]: PastDueLockout
}

class Lockout extends Component {
  render () {
    const self = this

    const {
      lockout, workspaceSlug, billingEmail, lockoutSettings, teamPlan,
      changeStep, addCreditCardAndUpdateBillingPlan, addCreditCardByWorkspaceSlug,
      churnSurvey, deleteWorkspace, updateChurnSurvey, workspace
    } = this.props
    const Lockout = lockouts[lockout.reason] || WarningLockout

    return (
      <div className='Lockout'>
        <div className='Lockout-overlay' />
        <Lockout
          message={lockout.message} // Only used for manual lockouts (WarningLockout)
          workspaceSlug={workspaceSlug}
          step={lockoutSettings.step}
          onStepChange={handleStepChange}
          onPlanClick={handlePlanClick}
          churnSurvey={churnSurvey}
          deleteWorkspace={deleteWorkspace}
          updateChurnSurvey={updateChurnSurvey}
          workspace={workspace}
        />
      </div>
    )

    function handleStepChange (step) {
      changeStep(step)

      if (step === 'credit_card' && lockout.reason === LOCKOUT_REASON_PAST_DUE) {
        addCreditCardByWorkspaceSlug(workspaceSlug, billingEmail)
        lockoutModalClicked({
          ...self.props.trackingProperties,
          lockoutType: LOCKOUT_REASON_PAST_DUE
        })
      }

      if (step === 'upgrade_prompt' && lockout.reason === LOCKOUT_REASON_MTUS) {
        lockoutModalClicked({
          ...self.props.trackingProperties,
          lockoutType: LOCKOUT_REASON_MTUS
        })
      }
    }

    function handlePlanClick (plan) {
      const id = plan === 'team' ? 'team-$100-1-month' : plan

      if (plan === 'team' && lockout.reason === LOCKOUT_REASON_MTUS) {
        addCreditCardAndUpdateBillingPlan(workspaceSlug, billingEmail, teamPlan)
      }

      lockoutOptionClicked({
        ...self.props.trackingProperties,
        lockoutType: LOCKOUT_REASON_PAST_DUE,
        lockoutOption: id
      })
    }
  }

  componentDidMount () {
    const reason = getProp('lockout.reason', this.props)
    if (reason === LOCKOUT_REASON_MTUS) this.props.fetchPlans()
    lockoutModalShown({
      ...this.props.trackingProperties,
      lockoutType: this.props.lockout.reason
    })
  }
}

/**
 * State -> props
 */

function mapStateToProps (state, ownProps) {
  const { workspaceSlug } = ownProps

  return {
    billingEmail: (getBilling(state, workspaceSlug) || {}).email,
    lockout: getLockoutByWorkspaceSlug(state, workspaceSlug),
    lockoutSettings: getLockoutsApp(state),
    plans: getSortedPlans(state),
    teamPlan: getPlanById(state, 'team-$100-1-month'),
    trackingProperties: {
      currentPlan: (getBillingPlan(state, workspaceSlug) || {}).id,
      workspaceId: (getWorkspace(state, workspaceSlug) || {}).id,
      workspaceSlug
    },
    churnSurvey: getChurnSurvey(state, 'lockoutDelete'),
    workspace: getActiveWorkspace(state)
  }
}

/**
 * Dispatch -> props
 */

function mapDispatchToProps (dispatch, ownProps) {
  const { workspaceSlug } = ownProps
  return {
    changeStep: (step) => dispatch(changeLockoutStep(step)),
    fetchLockouts: () => dispatch(fetchLockouts()),
    fetchPlans: () => dispatch(fetchPlans()),
    // Used for Past Due lockout
    addCreditCardByWorkspaceSlug: (workspaceSlug, billingEmail) => {
      const fail = () => dispatch(changeLockoutStep('initial_prompt'))
      const success = () => dispatch(fetchLockouts())

      dispatch(addCreditCardByWorkspaceSlug(workspaceSlug, billingEmail)).then(
        res => res.ok ? success() : fail(),
        () => fail()
      )
    },
    deleteWorkspace: () => dispatch(deleteWorkspaceBySlug(workspaceSlug)),
    updateChurnSurvey: data => dispatch(updateChurnSurvey({ key: 'lockoutDelete', data })),
    // Used for MTU lockout
    addCreditCardAndUpdateBillingPlan: (workspaceSlug, email, newPlan) => {
      const fail = () => dispatch(changeLockoutStep('upgrade_prompt'))
      const success = () => dispatch(fetchLockouts())

      dispatch(addCreditCardAndUpdateBillingPlan({ workspaceSlug, email, newPlan })).then(
        res => res.ok ? success() : fail(),
        () => fail()
      )
    }
  }
}

MtusLockout.propTypes = {
  churnSurvey: PropTypes.object.isRequired,
  step: PropTypes.string.isRequired,
  onStepChange: PropTypes.func.isRequired,
  onPlanClick: PropTypes.func.isRequired,
  updateChurnSurvey: PropTypes.func.isRequired,
  workspace: PropTypes.object.isRequired,
  deleteWorkspace: PropTypes.func.isRequired
}

MtusLockout.displayName = 'MtusLockout'

PastDueLockout.propTypes = {
  step: PropTypes.string.isRequired,
  onStepChange: PropTypes.func.isRequired
}

PastDueLockout.displayName = 'PastDueLockout'

WarningLockout.propTypes = {
  message: PropTypes.string.isRequired
}

WarningLockout.displayName = 'WarningLockout'

Lockout.defaultProps = {
  lockout: {}
}

Lockout.propTypes = {
  addCreditCardAndUpdateBillingPlan: PropTypes.func.isRequired,
  addCreditCardByWorkspaceSlug: PropTypes.func.isRequired,
  billingEmail: PropTypes.string.isRequired,
  changeStep: PropTypes.func.isRequired,
  fetchLockouts: PropTypes.func.isRequired,
  fetchPlans: PropTypes.func.isRequired,
  lockout: PropTypes.object.isRequired,
  lockoutSettings: PropTypes.object.isRequired,
  plan: PropTypes.string,
  teamPlan: PropTypes.object.isRequired,
  trackingProperties: PropTypes.object.isRequired,
  workspaceSlug: PropTypes.string.isRequired
}

Lockout.displayName = 'Lockout'

export default connect(mapStateToProps, mapDispatchToProps)(withBodyScrollDisabled(Lockout))



// WEBPACK FOOTER //
// ./ui/Lockout.js